
import $ from 'jquery';
import { ENDERECO_API } from '../../config/constant';
import { decode as rawBase64Decode, encode as base64_encode } from 'base-64';
import Swal from 'sweetalert2';
import { showDuplicateLoginAlert } from '../../components/Auth/DuplicateLoginAlert';

/**
 * Valida e prepara empresa para consulta de novas notas.
 * Se múltiplas empresas forem selecionadas, exibe aviso e retorna apenas a primeira.
 *
 * @param {Array|Object|Number} empresas - Empresa(s) selecionada(s)
 * @param {String} nomeEmpresaPrincipal - Nome da empresa que será consultada (opcional)
 * @returns {Promise<{empresaId: Number, prosseguir: Boolean}>}
 */
export const validarEmpresaConsulta = async (empresas, nomeEmpresaPrincipal = '') => {
  const normalizar = (empresa) => {
    if (typeof empresa === 'number') return empresa;
    return empresa?.id ?? empresa?.empresaId ?? empresa?.emp ?? empresa?.value ?? null;
  };

  let lista = [];
  if (Array.isArray(empresas)) {
    lista = empresas.map(normalizar).filter(Boolean);
  } else if (empresas) {
    lista = [normalizar(empresas)].filter(Boolean);
  }

  if (lista.length === 0) {
    await Swal.fire({
      icon: 'warning',
      title: 'Atenção',
      html: 'Nenhuma empresa selecionada.<br/>Selecione uma empresa para realizar a consulta.',
      confirmButtonColor: '#4099FF',
      confirmButtonText: 'OK',
    });
    return { empresaId: null, prosseguir: false };
  }

  if (lista.length === 1) {
    return { empresaId: lista[0], prosseguir: true };
  }

  const empresaEscolhida = lista[0];
  const textoEmpresa = nomeEmpresaPrincipal
    ? `<strong>${nomeEmpresaPrincipal}</strong>`
    : `<strong>empresa ${empresaEscolhida}</strong>`;

  const result = await Swal.fire({
    icon: 'info',
    title: 'Múltiplas Empresas Selecionadas',
    html: `
      Você selecionou <strong>${lista.length} empresas</strong>, mas a consulta de novas notas
      será realizada apenas para ${textoEmpresa}.
      <br/><br/>
      <small style="color: #666;">Para consultar outras empresas, selecione-as individualmente.</small>
    `,
    showCancelButton: true,
    confirmButtonColor: '#4099FF',
    cancelButtonColor: '#d33',
    confirmButtonText: 'Prosseguir',
    cancelButtonText: 'Cancelar',
  });

  return {
    empresaId: empresaEscolhida,
    prosseguir: result.isConfirmed,
  };
};

const normalizeHistoryNfseResponse = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  let normalizedValue = value.trim();

  if (normalizedValue.startsWith('"') && normalizedValue.endsWith('"')) {
    normalizedValue = JSON.parse(normalizedValue);
  }

  return normalizedValue;
};

const base64_decode = (value) => rawBase64Decode(normalizeHistoryNfseResponse(value));

const isHistoryNfseAuthError = (xhr) => {
  const candidates = [xhr?.message, xhr?.responseText, xhr?.responseJSON];

  return candidates.some((value) => {
    if (value === 'erro_auth') {
      return true;
    }

    if (typeof value !== 'string') {
      return false;
    }

    let normalizedValue = value.trim();

    if (normalizedValue.startsWith('"') && normalizedValue.endsWith('"')) {
      normalizedValue = JSON.parse(normalizedValue);
    }

    try {
      normalizedValue = base64_decode(normalizedValue);
    } catch (err) {
      return normalizedValue === 'erro_auth';
    }

    return normalizedValue === 'erro_auth';
  });
};

const handleHistoryNfseError = (xhr, reject, rejectMessage) => {
  if (isHistoryNfseAuthError(xhr)) {
    showDuplicateLoginAlert();
  }

  reject([400, { message: rejectMessage }]);
};

const getSession = () => {
  const usersRt = sessionStorage.getItem('ddesp');
  return usersRt !== null ? JSON.parse(base64_decode(usersRt)) : null;
};

const getSelectedCompany = (dataPost, emp) => {
  if (!dataPost) {
    return emp;
  }

  return dataPost.emp || dataPost.empresaId || dataPost.companyId || emp;
};

const ajaxHistoryNfseEvent = ({
  type,
  pp,
  ll,
  dd,
  tk,
  parseType = 'base64',
  rejectMessage = 'erro',
  extraData = {},
}) => new Promise(function (resolve, reject) {
  try {
    $.ajax({
      url: `${ENDERECO_API}src/api/controller/espiaoCtrl.class.php`,
      type,
      dataType: 'text',
      headers: {
        Authorization: 'Bearer ' + tk,
      },
      data: {
        pp: base64_encode(pp),
        ll: base64_encode(ll),
        dd: base64_encode(JSON.stringify(dd)),
        tk,
        ...extraData,
      },
      success: function (data) {
        if (parseType === 'jsonBase64') {
          resolve([200, JSON.parse(base64_decode(data))]);
          return;
        }

        if (parseType === 'base64') {
          resolve([200, base64_decode(data)]);
          return;
        }

        resolve([200, data]);
      },
      error: function (xhr) {
        handleHistoryNfseError(xhr, reject, rejectMessage);
      },
    });
  } catch (err) {
    reject([500, { message: err + ' Server Error' }]);
  }
});

const ajaxHistoryNfseLegacyEvent = ({
  type,
  act,
  key,
  ukey,
  opt,
  tk,
  parseType = 'raw',
  rejectMessage = 'erro',
}) => new Promise(function (resolve, reject) {
  try {
    $.ajax({
      url: `${ENDERECO_API}src/api/controller/historyNfse_controller.class.php`,
      type,
      dataType: 'json',
      headers: {
        Authorization: 'Bearer ' + tk,
      },
      data: {
        act,
        key,
        ukey,
        ...(opt !== undefined ? { opt } : {}),
        tk,
      },
      success: function (data) {
        if (parseType === 'jsonBase64') {
          resolve([200, JSON.parse(base64_decode(data))]);
          return;
        }

        if (parseType === 'base64') {
          resolve([200, base64_decode(data)]);
          return;
        }

        resolve([200, data]);
      },
      error: function (xhr) {
        handleHistoryNfseError(xhr, reject, rejectMessage);
      },
    });
  } catch (err) {
    reject([500, { message: err + ' Server Error' }]);
  }
});

const RestHistoryNfsePost = (config) => {
  const Arr_ = JSON.parse(config.data);
  const caminho = Arr_.dest.trim();
  const storage = getSession();
  const emp = storage[0];
  const uid = storage[3];
  const token = storage[14];
  const empresaSelecionada = getSelectedCompany(Arr_.dataPost, emp);
  const companyPayload = [
    base64_encode(empresaSelecionada),
    base64_encode(uid),
    base64_encode(JSON.stringify(Arr_.dataPost)),
  ];

  switch (caminho) {
    case 'dataIni':
      return ajaxHistoryNfseLegacyEvent({
        type: 'Get',
        act: 'dataIni',
        key: emp,
        ukey: uid,
        tk: token,
      });
    case 'pesqNfse':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'pesqNfse',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'pesqNfseNa':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'pesqNfseNa',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'salvaConfigTabela':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'updateConfigTabela',
        dd: [base64_encode(String(emp)), base64_encode(String(uid))],
        tk: token,
        parseType: 'raw',
        extraData: { opt: Arr_.dataPost },
      });
    case 'salvaConfigTabelaNa':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'updateConfigTabelaNa',
        dd: [base64_encode(String(emp)), base64_encode(String(uid))],
        tk: token,
        parseType: 'raw',
        extraData: { opt: Arr_.dataPost },
      });
    case 'salvaConfigTabelaAll':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'updateConfigTabelaAll',
        dd: [base64_encode(String(emp)), base64_encode(String(uid))],
        tk: token,
        parseType: 'raw',
        extraData: { opt: Arr_.dataPost },
      });
    case 'downXml':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'downXml',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'printDanfse':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'printDanfse',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'printDanfseCustom':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'printDanfseCustom',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'downloadDanfseCustomPdf':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'downloadDanfseCustomPdf',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'downloadDanfseZipFromHistory':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'downloadDanfseZipFromHistory',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'printDanfseCustomMunicipal':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'printDanfseCustomMunicipal',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'salvaNovoMarcador':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'salvaNovoMarcadorNfse',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'deleteMarcador':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'deleteMarcadores',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'hideMarcador':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'hideMarcadores',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'showMarcador':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'showMarcadores',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'setMarcador':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'setMarcador',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'downloadLoteTodas':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'downloadLoteTodas',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'downloadLote':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'downloadLote',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'enviaArquivosEmail':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'enviaArquivosEmail',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'enviaArquivosEmailTodos':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'enviaArquivosEmailTodos',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'pesqNfseAll':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'pesqNfseAll',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'salvaDataAuto':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'salvaDataAuto',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'buscarXmlsFaltantes':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'buscarXmlsFaltantes',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'analiseIa':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'analiseIaNfse',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'getQuotaHistoricoIa':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'getQuotaHistoricoIaNfse',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'consultarEventosNacional':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'consultarEventosNacional',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'consultarEventosNacionalLote':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'consultarEventosNacionalLote',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'manifestacaoTomador':
      return ajaxHistoryNfseEvent({
        type: 'Post',
        pp: 'nfse',
        ll: 'manifestacaoTomador',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    default:
      return new Promise((resolve, reject) => reject([404, { message: 'Rota não encontrada' }]));
  }
};

const RestHistoryNfseGet = (config) => {
  const caminho = config.dest.trim();
  const storage = getSession();
  const emp = storage[0];
  const uid = storage[3];
  const token = storage[14];
  const empresaSelecionada = getSelectedCompany(config.dataPost, emp);
  const companyPayload = [
    base64_encode(empresaSelecionada),
    base64_encode(uid),
    base64_encode(JSON.stringify(config.dataPost)),
  ];

  switch (caminho) {
    case 'getConfigTableNfse':
      return ajaxHistoryNfseEvent({
        type: 'Get',
        pp: 'nfse',
        ll: 'getConfigTableNfse',
        dd: [base64_encode(emp), base64_encode(uid)],
        tk: token,
      });
    case 'getConfigTableNfseNa':
      return ajaxHistoryNfseEvent({
        type: 'Get',
        pp: 'nfse',
        ll: 'getConfigTableNfseNa',
        dd: [base64_encode(emp), base64_encode(uid)],
        tk: token,
      });
    case 'getConfigTableNfseAll':
      return ajaxHistoryNfseEvent({
        type: 'Get',
        pp: 'nfse',
        ll: 'getConfigTableNfseAll',
        dd: [base64_encode(emp), base64_encode(uid)],
        tk: token,
      });
    case 'getMarcadores':
      return ajaxHistoryNfseEvent({
        type: 'Get',
        pp: 'nfse',
        ll: 'getMarcadores',
        dd: [base64_encode(emp), base64_encode(uid)],
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'getVinculadosNfse':
      return ajaxHistoryNfseLegacyEvent({
        type: 'Get',
        act: 'getVinculadosNfe',
        key: emp,
        ukey: uid,
        opt: config.dataPost,
        tk: token,
      });
    case 'getDataAuto':
      return ajaxHistoryNfseEvent({
        type: 'Get',
        pp: 'nfse',
        ll: 'getDataAuto',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'getUltimaConsulta':
      return ajaxHistoryNfseEvent({
        type: 'Get',
        pp: 'nfse',
        ll: 'getUltimaConsulta',
        dd: companyPayload,
        tk: token,
        parseType: 'jsonBase64',
      });
    case 'getCtribNacionalCabecas':
      return ajaxHistoryNfseEvent({
        type: 'Get',
        pp: 'nfse',
        ll: 'getCtribNacionalCabecas',
        dd: [base64_encode(emp), base64_encode(uid)],
        tk: token,
        parseType: 'jsonBase64',
      });
    default:
      return new Promise((resolve, reject) => reject([404, { message: 'Rota não encontrada' }]));
  }
};

/**
 * Consulta "Novas notas" garantindo apenas 1 empresa,
 * retornando metadados para o modal exibir o aviso.
 */
export async function consultarNovasNotas({ empresas, filtros, preferencia = 'primeira' }) {
  const normalizar = (empresa) => (typeof empresa === 'number' ? empresa : empresa?.id ?? empresa?.empresaId ?? empresa?.emp);
  let lista = [];
  if (Array.isArray(empresas)) {
    lista = empresas.map(normalizar).filter(Boolean);
  } else if (empresas) {
    lista = [normalizar(empresas)].filter(Boolean);
  }

  let empresaIdEscolhida = null;
  let multiSelecionado = false;

  if (lista.length > 1) {
    multiSelecionado = true;
    empresaIdEscolhida = preferencia === 'ativa' ? filtros?.empresaAtiva ?? lista[0] : lista[0];
  } else {
    empresaIdEscolhida = lista[0] ?? filtros?.empresaAtiva;
  }

  if (!empresaIdEscolhida) {
    throw new Error('Nenhuma empresa informada para consulta.');
  }

  const payload = {
    dest: 'pesqNfse',
    dataPost: { empresaId: empresaIdEscolhida, ...filtros },
  };

  const [status, data] = await RestHistoryNfsePost({
    data: JSON.stringify(payload),
  });

  return {
    status,
    data,
    meta: {
      empresaIdConsultada: empresaIdEscolhida,
      multiSelecionado,
      empresasSolicitadas: lista,
    },
  };
}

export { RestHistoryNfsePost, RestHistoryNfseGet };